/**
 * DataChannel framing layer.
 *
 * WebRTC DataChannels (SCTP/usrsctp in Chromium/Electron) reject single messages
 * larger than ~256 KiB — usrsctp returns EMSGSIZE and Chromium CLOSES the channel.
 * RPC envelopes are single JSON strings and can easily exceed that (e.g. a
 * `sessions:get` response for a workspace with hundreds of sessions), which made
 * public-access browser clients time out on `sessions:get`.
 *
 * This module splits every outbound message into size-bounded frames and reassembles
 * them on the other side. The DataChannel is created `ordered: true` (reliable +
 * in-order), so we need no acks / retransmits / out-of-order handling — just split
 * and join.
 *
 * Single source of truth: the actual algorithm lives in `datachannel-framing.mirror.js`
 * as plain JS, because the desktop bridge (apps/electron/src/main/public-access-bridge.ts)
 * runs its WebRTC peer as an injected JS string inside a hidden BrowserWindow and embeds
 * a verbatim copy of that file's `DC_FRAMING_MIRROR` region. A sync test asserts the
 * bridge copy matches the .js file. This .ts file only adds typed wrappers + constants.
 */

import * as mirror from './datachannel-framing.mirror.js'

export const FRAME_VERSION = 1
/** Conservative cross-browser-safe payload budget when SCTP max is unknown (WebRTC spec recommendation). */
export const DEFAULT_CHUNK_BUDGET_BYTES = 16 * 1024
/** Never exceed this even when SCTP negotiates a larger max — keeps bufferedAmount smooth. */
export const MAX_CHUNK_BUDGET_BYTES = 64 * 1024
/** Worst-case header byte length: "1|4294967295|999999|999999\n". */
export const FRAME_OVERHEAD_BYTES = 64
/** Reassembly guards against a malicious/buggy sender claiming a huge message. */
export const MAX_REASSEMBLY_BYTES = 32 * 1024 * 1024
export const MAX_REASSEMBLY_CHUNKS = 100_000

export interface FrameReassembler {
  /** Returns the fully reassembled original string when a message completes, else null. */
  push(frame: string): string | null
}

export interface ReassemblerOptions {
  maxBytes?: number
  maxChunks?: number
  onError?: (error: Error) => void
}

export function nextMsgId(prev: number): number {
  return mirror.__dcfNextMsgId(prev) as number
}

export function resolveChunkBudgetBytes(maxMessageSize: number | undefined | null): number {
  return mirror.__dcfResolveChunkBudgetBytes(maxMessageSize) as number
}

export function encodeFrames(message: string, msgId: number, budgetBytes: number): string[] {
  return mirror.__dcfEncodeFrames(message, msgId, budgetBytes) as string[]
}

export function createReassembler(opts?: ReassemblerOptions): FrameReassembler {
  return mirror.__dcfCreateReassembler(opts) as FrameReassembler
}
